import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { cameraService } from "client-app-core";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getTranslation } from "orion-components/i18n";
import { toast } from "react-toastify"; // cSpell:ignore toastify

const pad = value => String(value).padStart(2, "0");

const toDateTimeLocalValue = date => {
	const parsedDate = new Date(date);
	if (Number.isNaN(parsedDate.getTime())) {
		return "";
	}

	return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())}T${pad(
		parsedDate.getHours()
	)}:${pad(parsedDate.getMinutes())}:${pad(parsedDate.getSeconds())}`;
};

const getDefaultEndTime = startTime => new Date(startTime.getTime() + 30 * 1000);
const dateTimeLocalInputSx = {
	"& input::-webkit-calendar-picker-indicator": {
		cursor: "pointer",
		filter: "invert(1)"
	}
};

const toDate = value => {
	if (!value) {
		return null;
	}
	if (typeof value.toDate === "function") {
		return value.toDate();
	}
	return new Date(value);
};

const getRequestErrorMessage = (err, response, fallback) => {
	const reasonMessage = response?.reason?.message;
	return (
		err?.message ||
		response?.message ||
		(typeof reasonMessage === "string" ? reasonMessage : reasonMessage?.message) ||
		(typeof response?.reason === "string" ? response.reason : null) ||
		fallback
	);
};

const AddClipDialog = ({ open, cameraId, timestamp, onClose, container }) => {
	const [name, setName] = useState("");
	const [startTime, setStartTime] = useState("");
	const [endTime, setEndTime] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [requestError, setRequestError] = useState("");
	const initializedForOpenRef = useRef(false);
	const dialogContainer = useMemo(() => {
		const fullscreenElement = document.fullscreenElement;
		const usePlayerContainer =
			container && fullscreenElement && (fullscreenElement === container || fullscreenElement.contains(container));

		return usePlayerContainer ? () => container : undefined;
	}, [container, open]);

	useEffect(() => {
		if (!open) {
			initializedForOpenRef.current = false;
			return;
		}

		if (initializedForOpenRef.current || !timestamp) {
			return;
		}

		const initialStartTime = new Date(timestamp);
		if (Number.isNaN(initialStartTime.getTime())) {
			return;
		}

		setStartTime(toDateTimeLocalValue(initialStartTime));
		setEndTime(toDateTimeLocalValue(getDefaultEndTime(initialStartTime)));
		setName(initialStartTime.toLocaleString());
		initializedForOpenRef.current = true;
	}, [open, timestamp]);

	const getParsedTimes = () => {
		const parsedStartTime = toDate(startTime);
		const parsedEndTime = toDate(endTime);

		return {
			parsedStartTime,
			parsedEndTime,
			hasValidStartTime: parsedStartTime instanceof Date && !Number.isNaN(parsedStartTime.getTime()),
			hasValidEndTime: parsedEndTime instanceof Date && !Number.isNaN(parsedEndTime.getTime())
		};
	};

	const { parsedStartTime, parsedEndTime, hasValidStartTime, hasValidEndTime } = getParsedTimes();
	const hasValidRange = hasValidStartTime && hasValidEndTime && parsedEndTime > parsedStartTime;
	const canSave = cameraId && name.trim() && hasValidRange && !isSaving;

	const handleClose = () => {
		if (isSaving) {
			return;
		}

		setName("");
		setStartTime("");
		setEndTime("");
		setRequestError("");
		onClose();
	};

	const handleSave = () => {
		if (!canSave) {
			return;
		}

		const trimmedName = name.trim();
		setIsSaving(true);
		setRequestError("");

		cameraService.createVideoClip(
			cameraId,
			{
				name: trimmedName,
				description: trimmedName,
				startTime: parsedStartTime.toISOString(),
				endTime: parsedEndTime.toISOString()
			},
			(err, response) => {
				setIsSaving(false);

				if (err || response?.success === false) {
					const errorMessage = getRequestErrorMessage(
						err,
						response,
						getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.failedSaveClip")
					);
					setRequestError(errorMessage);
					toast.clearWaitingQueue();
					toast(errorMessage, {
						type: "error"
					});
					return;
				}

				EventBus.publish("CameraVideoClips_updated", { cameraId });
				handleClose();
			}
		);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			container={dialogContainer}
			sx={{ zIndex: 999999 }} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-dialog"
		>
			<DialogTitle data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-dialog-title">
				<Typography sx={{ fontWeight: 400, fontSize: "22px" }}>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.title")}
				</Typography>
			</DialogTitle>
			<DialogContent data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-dialog-content">
				<Typography sx={{ mb: 2 }}>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.description")}
				</Typography>
				<TextField
					autoFocus={true}
					fullWidth={true}
					label={getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.clipName")}
					value={name}
					onChange={event => {
						setRequestError("");
						setName(event.target.value);
					}}
					variant="standard"
					required={true}
					error={!name.trim()}
					sx={{ mb: 2 }} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-text-field"
				/>
				<TextField
					fullWidth={true}
					label={getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.startTime")}
					type="datetime-local"
					value={startTime}
					onChange={event => {
						setRequestError("");
						setStartTime(event.target.value);
					}}
					variant="standard"
					required={true}
					error={!hasValidStartTime}
					InputLabelProps={{ shrink: true }}
					inputProps={{ step: 1 }}
					sx={{ mb: 2, ...dateTimeLocalInputSx }} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-text-field-2"
				/>
				<TextField
					fullWidth={true}
					label={getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.endTime")}
					type="datetime-local"
					value={endTime}
					onChange={event => {
						setRequestError("");
						setEndTime(event.target.value);
					}}
					variant="standard"
					required={true}
					error={!hasValidEndTime || !hasValidRange}
					InputLabelProps={{ shrink: true }}
					inputProps={{ step: 1 }}
					sx={dateTimeLocalInputSx} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-text-field-3"
				/>
				{hasValidStartTime && hasValidEndTime && !hasValidRange && (
					<Typography sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}>
						{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.invalidRange")}
					</Typography>
				)}
				{requestError && (
					<Typography sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}>{requestError}</Typography>
				)}
			</DialogContent>
			<DialogActions data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-dialog-actions">
				<Button variant="text" onClick={handleClose} disabled={isSaving} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-button">
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.cancel")}
				</Button>
				<Button variant="text" onClick={handleSave} disabled={!canSave} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-clip-dialog-button-2">
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveClipDialog.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

AddClipDialog.propTypes = {
	open: PropTypes.bool,
	cameraId: PropTypes.string,
	timestamp: PropTypes.instanceOf(Date),
	onClose: PropTypes.func,
	container: PropTypes.any
};

AddClipDialog.defaultProps = {
	open: false,
	cameraId: null,
	timestamp: null,
	onClose: () => {},
	container: null
};

export default AddClipDialog;
